import { LocationSettingsContext } from '../context/context';

const LOCATION_STORAGE_KEY = 'atlas_last_location';
const LOCATION_SOURCE_KEY = 'atlas_location_source';
const MAP_VIEWPORT_STORAGE_KEY = 'atlas_last_map_viewport';

interface StoredLocation {
    location: LocationSettingsContext;
    timestamp: number;
    source: 'browser' | 'ip' | 'fallback';
}

interface StoredMapViewport {
    location: LocationSettingsContext;
    timestamp: number;
}

function mapViewportStorageKey(userId?: string | null): string {
    const keyPart = (userId || 'anonymous').trim() || 'anonymous';
    return `${MAP_VIEWPORT_STORAGE_KEY}:${keyPart}`;
}

/**
 * Store location in localStorage
 */
export const storeLocation = (location: LocationSettingsContext, source: 'browser' | 'ip' | 'fallback'): void => {
    try {
        const storedLocation: StoredLocation = {
            location,
            timestamp: Date.now(),
            source
        };
        
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(storedLocation));
        // console.log('Location stored in localStorage:', storedLocation);
    } catch (error) {
        console.warn('Failed to store location in localStorage:', error);
    }
};

/**
 * Retrieve stored location from localStorage
 */
export const getStoredLocation = (): StoredLocation | null => {
    try {
        const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (!stored) return null;

        const parsed = JSON.parse(stored) as StoredLocation;
        
        // Check if the stored location is less than 24 hours old
        const isRecent = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;
        
        if (!isRecent) {
            console.log('Stored location is too old, removing...');
            localStorage.removeItem(LOCATION_STORAGE_KEY);
            return null;
        }

        return parsed;
    } catch (error) {
        console.warn('Failed to retrieve location from localStorage:', error);
        return null;
    }
};

/**
 * Clear stored location
 */
export const clearStoredLocation = (): void => {
    try {
        localStorage.removeItem(LOCATION_STORAGE_KEY);
        console.log('Stored location cleared');
    } catch (error) {
        console.warn('Failed to clear stored location:', error);
    }
};

/**
 * Store the last viewed map viewport for a specific user.
 */
export const storeUserMapViewport = (
    userId: string | null | undefined,
    location: LocationSettingsContext
): void => {
    try {
        const payload: StoredMapViewport = {
            location,
            timestamp: Date.now(),
        };
        localStorage.setItem(mapViewportStorageKey(userId), JSON.stringify(payload));
    } catch (error) {
        console.warn('Failed to store map viewport in localStorage:', error);
    }
};

/**
 * Retrieve the last viewed map viewport for a specific user.
 */
export const getUserMapViewport = (
    userId: string | null | undefined
): StoredMapViewport | null => {
    try {
        const stored = localStorage.getItem(mapViewportStorageKey(userId));
        if (!stored) return null;
        const parsed = JSON.parse(stored) as StoredMapViewport;
        if (
            !parsed ||
            !parsed.location ||
            typeof parsed.location.latitude !== 'number' ||
            typeof parsed.location.longitude !== 'number' ||
            typeof parsed.location.zoom !== 'number'
        ) {
            return null;
        }
        return parsed;
    } catch (error) {
        console.warn('Failed to retrieve map viewport from localStorage:', error);
        return null;
    }
};

/**
 * Clear stored map viewport for a specific user.
 */
export const clearUserMapViewport = (userId: string | null | undefined): void => {
    try {
        localStorage.removeItem(mapViewportStorageKey(userId));
    } catch (error) {
        console.warn('Failed to clear map viewport from localStorage:', error);
    }
};

/**
 * Check if location storage is available
 */
export const isLocationStorageAvailable = (): boolean => {
    try {
        const test = '__test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch {
        return false;
    }
}; 
